import { useMutation } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputText from "@repo/components/src/forms/text";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import { Button, ButtonIcon } from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import { VStack } from "@repo/components/src/ui/vstack";
import { UpdateBrandPostulationTagDocument } from "@repo/graphql/documents/postulationTag/mutation.generated";
import { BrandPostulationTagsDocument } from "@repo/graphql/documents/postulationTag/query.generated";
import { Check, X } from "lucide-react-native";
import { useForm } from "react-hook-form";

import { Text } from "@repo/components/src/ui/text";
import ColorPicker from "./colorPicker";
import { TagFormSchema, tagFormSchema } from "./tagFormSchema";

interface EditTagFormProps {
  tagId: string;
  initialName: string;
  initialColor: string;
  onDone: () => void;
}

export default function EditTagForm({
  tagId,
  initialName,
  initialColor,
  onDone,
}: EditTagFormProps) {
  const form = useForm<TagFormSchema>({
    resolver: yupResolver(tagFormSchema),
    defaultValues: { name: initialName, color: initialColor },
  });

  const [updateTag, { loading }] = useMutation(
    UpdateBrandPostulationTagDocument,
    {
      refetchQueries: [BrandPostulationTagsDocument],
      onCompleted: (data) => {
        const errors = data.postulationTag.update.userErrors;
        if (errors && errors.length > 0) {
          MontuToastProvider.show({
            placement: "bottom",
            duration: 3000,
            render: ({ id }) => (
              <Toast nativeID={`toast-${id}`} action="error" variant="solid">
                <ToastDescription>{errors[0].message}</ToastDescription>
              </Toast>
            ),
          });
          return;
        }
        onDone();
      },
      onError: () => {
        MontuToastProvider.show({
          placement: "bottom",
          duration: 3000,
          render: ({ id }) => (
            <Toast nativeID={`toast-${id}`} action="error" variant="solid">
              <ToastDescription>Error al editar tag</ToastDescription>
            </Toast>
          ),
        });
      },
    },
  );

  const onSubmit = (data: TagFormSchema) => {
    updateTag({
      variables: { tagId, name: data.name, color: data.color },
    });
  };

  return (
    <VStack space="md" className="p-2 bg-background-50 rounded-lg">
      <Text size="sm">Editar tag "{initialName}"</Text>
      <FormInputText
        control={form.control}
        name="name"
        label={undefined}
        placeholder="Nombre del tag"
        required
        size="sm"
        keyboardType="default"
        textContentType="none"
        autoCapitalize="none"
      />
      <ColorPicker control={form.control} name="color" required size="sm" />
      <HStack space="xs">
        <Button
          size="xs"
          action="brand"
          isDisabled={loading}
          onPress={() => form.handleSubmit(onSubmit)()}
        >
          <ButtonIcon as={Check} />
        </Button>
        <Button size="xs" variant="outline" action="muted" onPress={onDone}>
          <ButtonIcon as={X} />
        </Button>
      </HStack>
    </VStack>
  );
}
